import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  BookOpenText,
  CircleDollarSign,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  X
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { GistlyMark } from "./GistlyMark";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const navItems = [
  {
    to: "/new",
    label: "สร้างสรุปใหม่",
    description: "เพิ่ม YouTube หรือไฟล์",
    icon: Plus
  },
  {
    to: "/library",
    label: "คลังความรู้",
    description: "ค้นหาและอ่านบทสรุป",
    icon: BookOpenText
  },
  {
    to: "/tasks",
    label: "งานประมวลผล",
    description: "ติดตามงานเบื้องหลัง",
    icon: Activity
  },
  {
    to: "/costs",
    label: "ค่าใช้จ่าย",
    description: "Tokens และ API usage",
    icon: CircleDollarSign
  }
];

function getPageTitle(pathname: string): string {
  if (pathname.startsWith("/library")) return "คลังความรู้";
  if (pathname.startsWith("/tasks")) return "งานประมวลผล";
  if (pathname.startsWith("/costs")) return "ค่าใช้จ่าย";
  return "สร้างสรุปใหม่";
}

const RAIL_STORAGE_KEY = "gistly:sidebar-rail";

function readStoredRail(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const title = getPageTitle(location.pathname);

  // Reading a single summary is a focus mode: the sidebar folds to a rail so the
  // document gets the width, then returns to whatever the user normally prefers.
  const readerOpen = /^\/library\/.+/.test(location.pathname);
  // Landing straight on a summary URL should open in focus mode too
  const [rail, setRail] = useState(() => readStoredRail() || readerOpen);
  const preferredRail = useRef(readStoredRail());
  const wasReaderOpen = useRef(readerOpen);

  useEffect(() => {
    if (readerOpen === wasReaderOpen.current) return;
    wasReaderOpen.current = readerOpen;
    setRail(readerOpen ? true : preferredRail.current);
  }, [readerOpen]);

  const toggleRail = useCallback(() => {
    setRail((current) => {
      const next = !current;
      // Only a toggle outside the reader changes the long-term preference,
      // so focus mode never silently rewrites how the app opens next time.
      if (!wasReaderOpen.current) {
        preferredRail.current = next;
        try {
          window.localStorage.setItem(RAIL_STORAGE_KEY, next ? "1" : "0");
        } catch {
          /* private mode — the toggle still works for this session */
        }
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        return;
      }
      if (event.key.toLowerCase() !== "b" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      toggleRail();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggleRail]);

  // Poll tasks so a badge shows how many jobs are running from anywhere in the app
  const tasksQuery = useQuery({
    queryKey: ["tasks"],
    queryFn: api.getTasks,
    refetchInterval: (query) =>
      query.state.data?.some((task) => task.status === "running") ? 2000 : 20000
  });
  const runningCount = (tasksQuery.data ?? []).filter((task) => task.status === "running").length;

  const { user, logout } = useAuth();
  const usageQuery = useQuery({ queryKey: ["usage"], queryFn: api.getUsage, refetchInterval: 30000 });
  const usage = usageQuery.data;

  return (
    <div className={`app-shell ${rail ? "sidebar-rail" : ""}`}>
      <aside id="app-sidebar" className={`sidebar ${mobileOpen ? "sidebar-open" : ""}`}>
        <div className="brand">
          <NavLink
            className="brand-mark"
            to="/new"
            aria-label="Gistly, กลับหน้าสร้างสรุปใหม่"
            title="กลับหน้าสร้างสรุปใหม่"
            onClick={() => setMobileOpen(false)}
          >
            <GistlyMark size={24} />
          </NavLink>
          <div className="brand-text">
            <strong>Gistly</strong>
            <span>Get the gist · อ่าน · เก็บไว้</span>
          </div>
          <button
            className="icon-button sidebar-toggle"
            type="button"
            aria-label={rail ? "ขยายเมนู" : "ย่อเมนู"}
            aria-expanded={!rail}
            title={`${rail ? "ขยายเมนู" : "ย่อเมนู"} (⌘B)`}
            onClick={toggleRail}
          >
            {rail ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
          <button
            className="icon-button sidebar-close"
            type="button"
            aria-label="ปิดเมนู"
            onClick={() => setMobileOpen(false)}
          >
            <X size={19} />
          </button>
        </div>

        <nav className="main-nav" aria-label="เมนูหลัก">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                title={rail ? item.label : undefined}
                onClick={() => setMobileOpen(false)}
              >
                <span className="nav-icon">
                  <Icon size={19} />
                </span>
                <span className="nav-text">
                  <strong>{item.label}</strong>
                  <small>{item.description}</small>
                </span>
                {item.to === "/tasks" && runningCount > 0 && (
                  <span className="nav-badge" aria-label={`${runningCount} งานกำลังทำงาน`}>
                    {runningCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="sidebar-user">
          {user?.picture ? (
            <img className="sidebar-avatar" src={user.picture} alt={user.name || "ผู้ใช้"} />
          ) : (
            <span className="sidebar-avatar placeholder">{(user?.name || "?").slice(0, 1)}</span>
          )}
          <div className="sidebar-user-info">
            <strong>{user?.name || "ผู้ใช้"}</strong>
            <small>{usage ? (usage.unlimited ? "ผู้ดูแล · ไม่จำกัด" : `ใช้ไป ${usage.used}/${usage.limit} เดือนนี้`) : "Gistly"}</small>
          </div>
          <button
            type="button"
            className="icon-button sidebar-logout"
            aria-label="ออกจากระบบ"
            title="ออกจากระบบ"
            onClick={logout}
          >
            <LogOut size={17} />
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <button
          className="sidebar-backdrop"
          type="button"
          aria-label="ปิดเมนู"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="app-main">
        <header className="topbar">
          <div className="topbar-title">
            <button
              className="icon-button mobile-menu"
              type="button"
              aria-label="เปิดเมนู"
              aria-controls="app-sidebar"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={20} />
            </button>
            <div>
              <p>Gistly</p>
              <h1>{title}</h1>
            </div>
          </div>
          <div className="topbar-badge">
            <span className="status-dot" />
            <span>{usage ? (usage.unlimited ? "ผู้ดูแล · ไม่จำกัด" : `${usage.used}/${usage.limit} บทสรุปเดือนนี้`) : "Cloud workspace"}</span>
          </div>
        </header>

        <main className="page-container">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
